import i18n from "@/setup/i18n";
import { oidcSettings } from "@/setup/OIDCSettings";
import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";
import AosComponents from "@socotec.io/socio-aos-component";
import { getDefaultSentryConfig } from "@/setup/sentry";
import SocioVueComponents, {
  setTelemetryUser,
  initializeTracingAndLogging,
} from "@socotec.io/socio-vue-components";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { WebStorageStateStore } from "oidc-client-ts";
import { createPinia } from "pinia";
import { createORM } from "pinia-orm";
import { createApp } from "vue";
import VueSignaturePad from "vue-signature-pad";
import { createOidc, useOidcStore } from "vue3-oidc";
import PackageJson from "../package.json";
import SocioManifest from "../socio-manifest.json";
import App from "./App.vue";
import router from "./router";
import vuetify from "./setup/vuetify";
import { useNotificationsStore } from "./store/modules/socio-store/notification.js";

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

createOidc({
  oidcSettings: oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      useOidcStore().actions.value.setUser(user);
      setTelemetryUser(user, faroSettings.enableTelemetry);
    },
  },
  userStore: new WebStorageStateStore({
    prefix: "vue3-oidc",
    store: window.sessionStorage,
  }),
});

dayjs.extend(utc);
dayjs.extend(timezone);

const app = createApp(App);

const pinia = createPinia().use(createORM());
pinia.use(createSentryPiniaPlugin());
app.use(pinia);

const envConfig = {
  i18nLocale: import.meta.env.VITE_APP_I18N_LOCALE,
  i18nFallbackLocale: import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE,
  mapBoxApiKey: import.meta.env.VITE_APP_MAP_BOX,
  notificationsStore: useNotificationsStore(),
  modelLinkerApiConfig: {
    basePath: import.meta.env.VITE_APP_MODEL_LINKER_REST_API_URL,
    accessToken: () => useOidcStore().state.value.token,
  },
  aosBackApiConfig: {
    basePath: import.meta.env.VITE_APP_AOS_REST_API_URL,
    accessToken: () => useOidcStore().state.value.token,
  },
};

app.use(router);

app.use(AosComponents, { i18n, envConfig });
initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});

app.config.globalProperties.$dayjs = dayjs;

// Set global properties
app.use(VueSignaturePad);
app.use(SocioVueComponents, i18n);

// Initialize Sentry
Sentry.init({
  ...getDefaultSentryConfig({
    app,
    dsn: "https://62921d7b5861e529266c277e9d3d58b5@sentry.tools.infra-socotec.net/20",
    router,
    enabled: import.meta.env.MODE !== "development",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
    release: `atm@${import.meta.env.VITE_APP_VERSION}`,
  }),
});

app.use(i18n);
app.use(vuetify);
app.mount("#app");
